import React, { useState } from "react";
import { Trash2, Plus, ClipboardPaste, X } from "lucide-react";
import { speedToPace, parsePastedRows } from "../lib/pace";

const cellCls =
  "w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-2 py-2 text-sm font-mono-tir text-[var(--tir-ink)] focus:outline-none";

export default function TestDataTable({ rows, speedUnit, onChange }) {
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteMode, setPasteMode] = useState("replace"); // or "append"

  const updateRow = (idx, field, value) => {
    const next = rows.map((r, i) => {
      if (i !== idx) return r;
      const updated = { ...r, [field]: value };
      // Auto-calc pace whenever speed changes
      if (field === "speed") {
        updated.pace = speedToPace(value, speedUnit);
      }
      return updated;
    });
    onChange(next);
  };

  const addRow = () => {
    const lastTime = rows.length > 0 ? Number(rows[rows.length - 1].time) || 0 : 0;
    onChange([
      ...rows,
      { time: lastTime + 4, pace: "", speed: "", hr: "", blood_lactate: "", rpe: "" },
    ]);
  };
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

  // Handle paste directly into any cell — if it looks like a multi-cell/multi-row paste, expand.
  const handleCellPaste = (e, idx) => {
    const text = e.clipboardData?.getData("text") || "";
    const isMulti = text.includes("\n") || text.includes("\t");
    if (!isMulti) return; // fall through to default single-cell paste
    e.preventDefault();
    const incoming = parsePastedRows(text);
    if (incoming.length === 0) return;
    const merged = [...rows];
    for (let i = 0; i < incoming.length; i++) {
      const target = idx + i;
      const existing = merged[target] || { time: "", pace: "", speed: "", hr: "", blood_lactate: "", rpe: "" };
      const inc = incoming[i];
      const next = { ...existing };
      for (const key of ["time", "pace", "speed", "hr", "blood_lactate", "rpe"]) {
        if (inc[key] !== "" && inc[key] != null) next[key] = inc[key];
      }
      // if speed provided but pace blank in paste, auto-calc
      if ((!inc.pace || inc.pace === "") && next.speed !== "" && next.speed != null) {
        next.pace = speedToPace(next.speed, speedUnit);
      }
      merged[target] = next;
    }
    onChange(merged);
  };

  const applyBulkPaste = () => {
    const incoming = parsePastedRows(pasteText);
    if (incoming.length === 0) return;
    // Auto-fill pace whenever missing
    const withPace = incoming.map((r) => ({
      ...r,
      pace: r.pace || speedToPace(r.speed, speedUnit),
    }));
    if (pasteMode === "replace") {
      onChange(withPace);
    } else {
      onChange([...rows, ...withPace]);
    }
    setPasteText("");
    setPasteOpen(false);
  };

  return (
    <div className="w-full" data-testid="lactate-profile-table">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)] font-semibold">
            Running Lactate Profile
          </div>
          <div className="font-serif-display text-xl text-[var(--tir-ink)] mt-0.5">
            Stage-by-stage data
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => setPasteOpen(true)}
            data-testid="paste-data-btn"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-[var(--tir-ink)] hover:text-[var(--tir-burgundy)] transition-colors"
          >
            <ClipboardPaste size={14} /> Paste data
          </button>
          <button
            type="button"
            onClick={addRow}
            data-testid="add-row-btn"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] transition-colors"
          >
            <Plus size={14} /> Add stage
          </button>
        </div>
      </div>

      {pasteOpen && (
        <div
          className="mb-4 border border-[var(--tir-ink)] bg-white p-5"
          data-testid="paste-dialog"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="font-serif-display text-xl text-[var(--tir-ink)]">
                Paste from spreadsheet
              </div>
              <p className="text-xs text-[var(--tir-muted)] mt-1 leading-relaxed max-w-xl">
                Copy cells from Excel / Google Sheets / Numbers. Expected column order:{" "}
                <span className="font-mono-tir">
                  Time · Pace · Speed ({speedUnit}) · HR · Blood&nbsp;Lactate · RPE
                </span>
                . Leave Pace blank and it will auto-fill from Speed.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setPasteOpen(false)}
              className="text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
              aria-label="Close"
              data-testid="close-paste-dialog"
            >
              <X size={16} />
            </button>
          </div>
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            rows={8}
            placeholder={`4\t\t4.2\t112\t1.1\t2\n8\t\t4.9\t134\t1.8\t3.5`}
            className="w-full bg-[var(--tir-ivory)] border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] p-3 text-sm font-mono-tir focus:outline-none"
            data-testid="paste-textarea"
            autoFocus
          />
          <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
            <div
              className="inline-flex border border-[var(--tir-ink)] h-8"
              data-testid="paste-mode-toggle"
            >
              {[
                ["replace", "Replace rows"],
                ["append", "Append rows"],
              ].map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setPasteMode(val)}
                  data-testid={`paste-mode-${val}`}
                  className={`px-3 text-[10px] tracking-[0.22em] uppercase font-semibold transition-colors ${
                    pasteMode === val
                      ? "bg-[var(--tir-ink)] text-white"
                      : "bg-white text-[var(--tir-ink)] hover:bg-[var(--tir-ivory)]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPasteOpen(false)}
                className="text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
                data-testid="cancel-paste-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyBulkPaste}
                disabled={!pasteText.trim()}
                data-testid="apply-paste-btn"
                className="bg-[var(--tir-burgundy)] text-white px-5 py-2 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors disabled:opacity-40"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full overflow-x-auto border border-[var(--tir-border)] bg-white">
        <table className="w-full text-left border-collapse min-w-[640px]">
          <thead>
            <tr className="bg-[#EFE9E6]">
              <th className="px-2 py-3 text-[10px] uppercase tracking-[0.18em] text-[var(--tir-ink)] font-semibold border-b-2 border-[var(--tir-ink)]">
                Time (min)
              </th>
              <th className="px-2 py-3 text-[10px] uppercase tracking-[0.18em] text-[var(--tir-ink)] font-semibold border-b-2 border-[var(--tir-ink)]">
                Pace (min/km)
              </th>
              <th className="px-2 py-3 text-[10px] uppercase tracking-[0.18em] text-[var(--tir-ink)] font-semibold border-b-2 border-[var(--tir-ink)]">
                Speed ({speedUnit})
              </th>
              <th className="px-2 py-3 text-[10px] uppercase tracking-[0.18em] text-[var(--tir-ink)] font-semibold border-b-2 border-[var(--tir-ink)]">
                HR (bpm)
              </th>
              <th className="px-2 py-3 text-[10px] uppercase tracking-[0.18em] text-[var(--tir-ink)] font-semibold border-b-2 border-[var(--tir-ink)]">
                Lactate (mmol/L)
              </th>
              <th className="px-2 py-3 text-[10px] uppercase tracking-[0.18em] text-[var(--tir-ink)] font-semibold border-b-2 border-[var(--tir-ink)]">
                RPE
              </th>
              <th className="px-2 py-3 border-b-2 border-[var(--tir-ink)] w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx} data-testid={`row-${idx}`} className="hover:bg-[var(--tir-ivory)]">
                <td className="align-top">
                  <input
                    type="number"
                    value={r.time ?? ""}
                    onChange={(e) => updateRow(idx, "time", e.target.value)}
                    onPaste={(e) => handleCellPaste(e, idx)}
                    className={cellCls}
                    data-testid={`row-${idx}-time`}
                  />
                </td>
                <td className="align-top">
                  <input
                    type="text"
                    value={r.pace ?? ""}
                    placeholder="auto"
                    onChange={(e) => updateRow(idx, "pace", e.target.value)}
                    onPaste={(e) => handleCellPaste(e, idx)}
                    className={cellCls}
                    data-testid={`row-${idx}-pace`}
                  />
                </td>
                <td className="align-top">
                  <input
                    type="number"
                    step="0.1"
                    value={r.speed ?? ""}
                    onChange={(e) => updateRow(idx, "speed", e.target.value)}
                    onPaste={(e) => handleCellPaste(e, idx)}
                    className={cellCls}
                    data-testid={`row-${idx}-speed`}
                  />
                </td>
                <td className="align-top">
                  <input
                    type="number"
                    value={r.hr ?? ""}
                    onChange={(e) => updateRow(idx, "hr", e.target.value)}
                    onPaste={(e) => handleCellPaste(e, idx)}
                    className={cellCls}
                    data-testid={`row-${idx}-hr`}
                  />
                </td>
                <td className="align-top">
                  <input
                    type="number"
                    step="0.1"
                    value={r.blood_lactate ?? ""}
                    onChange={(e) => updateRow(idx, "blood_lactate", e.target.value)}
                    onPaste={(e) => handleCellPaste(e, idx)}
                    className={cellCls}
                    data-testid={`row-${idx}-lactate`}
                  />
                </td>
                <td className="align-top">
                  <input
                    type="number"
                    step="0.5"
                    value={r.rpe ?? ""}
                    onChange={(e) => updateRow(idx, "rpe", e.target.value)}
                    onPaste={(e) => handleCellPaste(e, idx)}
                    className={cellCls}
                    data-testid={`row-${idx}-rpe`}
                  />
                </td>
                <td className="align-top text-right">
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    data-testid={`row-${idx}-delete`}
                    className="p-2 text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors"
                    aria-label="Remove row"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-sm text-[var(--tir-muted)] py-6">
                  No stages yet. Add your first data point or paste from a spreadsheet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[var(--tir-muted)] mt-2">
        Pace auto-calculates from speed. You can also paste multiple cells or rows from Excel /
        Sheets directly into any cell.
      </p>
    </div>
  );
}
